import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Shield, Lock } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

interface PolicySection {
  id: string;
  section_number: number;
  title: string;
  description: string;
  content: {
    subsections: {
      title: string;
      content: string;
    }[];
  };
}

export default function Tier1PolicySection() {
  const { sectionNumber } = useParams();
  const sectionNum = parseInt(sectionNumber || "1");
  const [section, setSection] = useState<PolicySection | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalSections, setTotalSections] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    loadSection();
  }, [sectionNum]);

  const loadSection = async () => {
    try {
      setLoading(true);
      
      // Fetch the specific section
      const { data: sectionData, error: sectionError } = await supabase
        .from('tier1_policy_sections')
        .select('*')
        .eq('section_number', sectionNum)
        .single();

      if (sectionError) throw sectionError;
      
      // Fetch total count for navigation
      const { count } = await supabase
        .from('tier1_policy_sections')
        .select('*', { count: 'exact', head: true });

      setTotalSections(count || 0);
      setSection(sectionData as unknown as PolicySection);
    } catch (error) {
      console.error('Error loading section:', error);
      toast({
        title: "Error",
        description: "Failed to load policy section",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center">
        <p className="text-muted-foreground">Loading section...</p>
      </div>
    );
  }

  if (!section) {
    return <Navigate to="/partner/policy-centre/tier-1" replace />;
  }

  const prevSection = sectionNum > 1 ? sectionNum - 1 : null;
  const nextSection = sectionNum < totalSections ? sectionNum + 1 : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/partner/policy-centre/tier-1"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Policy Overview
          </Link>
          
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-6 w-6 text-primary" />
            <Badge variant="outline">Section {section.section_number}</Badge>
            <Badge variant="destructive" className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              CONFIDENTIAL
            </Badge>
          </div>
          
          <h1 className="text-3xl font-bold text-foreground mb-2">{section.title}</h1>
          <p className="text-muted-foreground">{section.description}</p>
        </div>

        <Separator className="my-6" />

        {/* Content */}
        {section.content.subsections && section.content.subsections.length > 0 ? (
          <div className="space-y-4 mb-8">
            {section.content.subsections.map((subsection, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-lg">{subsection.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="prose prose-slate dark:prose-invert max-w-none 
                      [&_p]:min-h-[1.5em]
                      [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:my-2
                      [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:my-2
                      [&_li]:my-1
                      [&_table]:border-collapse [&_table]:border [&_table]:border-border 
                      [&_td]:border [&_td]:border-border [&_td]:p-2 
                      [&_th]:border [&_th]:border-border [&_th]:p-2 [&_th]:bg-muted"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(subsection.content) }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="mb-8">
            <CardContent className="pt-6">
              <p className="text-muted-foreground">No content available for this section yet.</p>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center">
          {prevSection ? (
            <Button variant="outline" asChild>
              <Link to={`/partner/policy-centre/tier-1/section/${prevSection}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Previous Section
              </Link>
            </Button>
          ) : (
            <div />
          )}
          
          {nextSection ? (
            <Button variant="outline" asChild>
              <Link to={`/partner/policy-centre/tier-1/section/${nextSection}`}>
                Next Section
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}